import { useEffect, useMemo } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Area, Bootstrap, IconAsset, Project, Settings, Space, TaskPage, TaskQuery, TaskWithFlags, TodayView } from "@desk/contracts";
import { useApi, useMinuteClock } from "./context";
import { qk } from "./cache";
import { useOverlay } from "./outbox";
import { applyPatch, applyPatches, type Overlay } from "./outbox-core";
import { applyContainerOverlay, pruneContainerOverlay, fetchStartedAt, useContainerOverlay, type Stamped } from "./containers";
import { dedupe } from "@/lib/filters";
import { localZone, todayIn } from "@/lib/dates";

export function useBootstrap(options: { refetchOnMount?: boolean | "always" } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: qk.bootstrap,
    queryFn: async (): Promise<Stamped<Bootstrap>> => {
      const started = Date.now();
      return { ...(await api.bootstrap()), fetchStartedAt: started };
    },
    staleTime: 30_000,
    ...options,
  });
}

export interface Hierarchy {
  areas: Area[];
  spaces: Space[];
  projects: Project[];
  area: (id: string | null | undefined) => Area | undefined;
  space: (id: string | null | undefined) => Space | undefined;
  project: (id: string | null | undefined) => Project | undefined;
  spacesOf: (areaId: string) => Space[];
  /** Spaces that aren't inside an area, listed above the areas. */
  topSpaces: Space[];
  projectsOf: (spaceId: string) => Project[];
  /** Imported icon by id (areas and spaces reference these). */
  asset: (id: string | null | undefined) => IconAsset | undefined;
  assets: IconAsset[];
  /** Settled, completed (projects), or sidebar-snoozed (still in the future). */
  isResting: (c: Space | Project) => boolean;
  settings: Settings | undefined;
  timezone: string;
  today: string;
  loaded: boolean;
}

const byPosition = <T extends { position: number; name: string }>(a: T, b: T) => a.position - b.position || a.name.localeCompare(b.name);

export function useHierarchy(): Hierarchy {
  const qc = useQueryClient();
  const { data } = useBootstrap();
  const now = useMinuteClock();
  const pending = useContainerOverlay();
  const startedAt = fetchStartedAt(data);
  useEffect(() => pruneContainerOverlay(qc), [qc, startedAt]);
  return useMemo(() => {
    const live = <T extends { id: string; archivedAt: string | null; position: number; name: string }>(kind: "area" | "space" | "project", list: T[] | undefined) =>
      applyContainerOverlay(kind, list ?? [], pending, startedAt)
        .filter((c) => !c.archivedAt)
        .sort(byPosition);
    const areas = live("area", data?.areas);
    const spaces = live("space", data?.spaces);
    const am = new Map(areas.map((a) => [a.id, a]));
    const sm = new Map(spaces.map((s) => [s.id, s]));
    // Space moves cascade to projects on the server. Derive their ancestry from the same
    // overlaid space here, so pickers and new-task drafts never capture inconsistent parents.
    const projects = live("project", data?.projects).map((p) => {
      const space = sm.get(p.spaceId);
      return space && space.areaId !== p.areaId ? { ...p, areaId: space.areaId } : p;
    });
    const pm = new Map(projects.map((p) => [p.id, p]));
    const assets = new Map((data?.assets ?? []).map((a) => [a.id, a]));
    const timezone = data?.settings?.timezone || localZone();
    const nowIso = new Date(now).toISOString();
    return {
      areas,
      spaces,
      projects,
      area: (id) => (id ? am.get(id) : undefined),
      space: (id) => (id ? sm.get(id) : undefined),
      project: (id) => (id ? pm.get(id) : undefined),
      spacesOf: (areaId) => spaces.filter((s) => s.areaId === areaId),
      topSpaces: spaces.filter((s) => !s.areaId),
      projectsOf: (spaceId) => projects.filter((p) => p.spaceId === spaceId),
      asset: (id) => (id ? assets.get(id) : undefined),
      assets: [...assets.values()],
      // G19: a completed project is settled even if it predates settle-on-complete.
      isResting: (c) => !!c.settledAt || ("state" in c && c.state === "completed") || (!!c.snoozedUntil && c.snoozedUntil > nowIso),
      settings: data?.settings,
      timezone,
      today: todayIn(timezone),
      loaded: !!data,
    };
  }, [data, now, pending, startedAt]);
}

/** "HumVision › Windows release" style label for a task's current location. */
export function locationLabel(t: Pick<TaskWithFlags, "areaId" | "spaceId" | "projectId">, h: Hierarchy): string {
  if (!t.areaId && !t.spaceId) return "Inbox";
  const p = h.project(t.projectId);
  const s = h.space(t.spaceId);
  if (p && s) return `${s.name} › ${p.name}`;
  if (s) return s.name;
  return h.area(t.areaId)?.name ?? "Unknown";
}

function withOverlay(tasks: TaskWithFlags[], overlay: Overlay, match?: (t: TaskWithFlags) => boolean): TaskWithFlags[] {
  const patched = applyPatches(tasks, overlay);
  const creates = match ? overlay.creates.filter(match) : [];
  return dedupe([...patched, ...creates]);
}

/**
 * Task list for a query, with the outbox overlay applied. `matchCreate` decides which locally
 * created (unsynced) tasks belong in this list.
 */
export function useTaskList(query: TaskQuery, matchCreate?: (t: TaskWithFlags) => boolean, enabled = true) {
  const api = useApi();
  const overlay = useOverlay();
  const q = useQuery({
    queryKey: qk.tasks(query),
    // Every page: lists, counts, and completion dialogs must not stop at the first 500 tasks.
    queryFn: async ({ signal }): Promise<TaskPage> => ({ items: await api.listAllTasks(query, Infinity, signal), nextCursor: null }),
    placeholderData: keepPreviousData,
    enabled,
  });
  const page = q.data as TaskPage | undefined;
  const tasks = useMemo(() => withOverlay(page?.items ?? [], overlay, matchCreate), [page, overlay, matchCreate]);
  return { ...q, tasks };
}

export function useTask(idOrReadable: string | null | undefined) {
  const api = useApi();
  const overlay = useOverlay();
  const pending = idOrReadable ? overlay.creates.find((c) => c.id === idOrReadable) : undefined;
  const q = useQuery({
    queryKey: qk.task(idOrReadable ?? ""),
    queryFn: () => api.getTask(idOrReadable!),
    enabled: !!idOrReadable && !pending,
  });
  const task = useMemo(() => (pending ? pending : q.data ? applyPatch(q.data, overlay) : undefined), [pending, q.data, overlay]);
  return { ...q, task };
}

export function useToday(date: string, tz: string) {
  const api = useApi();
  const overlay = useOverlay();
  const q = useQuery({ queryKey: qk.today(date, tz), queryFn: () => api.today(date, tz), placeholderData: keepPreviousData });
  const view = useMemo<TodayView | undefined>(() => {
    if (!q.data) return undefined;
    const patchRows = (rows: TodayView["planned"]) =>
      rows
        .map((r) => ({ ...r, task: applyPatch(r.task, overlay) }))
        .filter((r) => !r.task.deletedAt);
    const planChanges = overlay.plan.get(date);
    let planned = patchRows(q.data.planned);
    if (planChanges) planned = planned.filter((r) => planChanges.get(r.task.id) !== false);
    // Locally created tasks planned for today.
    const extra = overlay.creates.filter((c) => c.plannedDate === date && !planned.some((r) => r.task.id === c.id));
    planned = [...planned, ...extra.map((task) => ({ task, carriedFrom: null }))];
    const all = [...planned];
    const completedNow = all.filter((r) => r.task.status === "done" || r.task.status === "cancelled");
    const completedIds = new Set(completedNow.map((r) => r.task.id));
    const reopened = patchRows(q.data.completed).filter((r) => r.task.status !== "done" && r.task.status !== "cancelled");
    return {
      ...q.data,
      planned: [...planned.filter((r) => !completedIds.has(r.task.id)), ...reopened],
      carryover: patchRows(q.data.carryover).filter((r) => planChanges?.get(r.task.id) !== false),
      due: patchRows(q.data.due),
      completed: dedupe(
        [...completedNow, ...patchRows(q.data.completed).filter((r) => r.task.status === "done" || r.task.status === "cancelled")].map(
          (r) => ({ ...r, id: r.task.id }),
        ),
      ),
    };
  }, [q.data, overlay, date]);
  return { ...q, view };
}

/** A view query with unsynced local edits (outbox overlay) applied, like task lists. */
function useOverlaid<Q extends { data: unknown }>(q: Q, patch: (data: NonNullable<Q["data"]>, overlay: Overlay) => NonNullable<Q["data"]>): Q {
  const overlay = useOverlay();
  const data = useMemo(() => (q.data == null ? q.data : patch(q.data as NonNullable<Q["data"]>, overlay)), [q.data, overlay]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...q, data };
}

export function useUpcoming(from: string, days = 14) {
  const api = useApi();
  const q = useQuery({ queryKey: [...qk.upcoming(from), days], queryFn: () => api.upcoming(from, days), placeholderData: keepPreviousData });
  return useOverlaid(q, (v, o) => ({
    ...v,
    days: v.days.map((d) => ({ ...d, planned: applyPatches(d.planned, o), deadlines: applyPatches(d.deadlines, o) })),
  }));
}

export function useReview() {
  const api = useApi();
  const q = useQuery({ queryKey: qk.review, queryFn: () => api.review(), refetchInterval: 5 * 60_000 });
  return useOverlaid(q, (v, o) => ({
    ...v,
    items: v.items.map((i) => ({ ...i, task: applyPatch(i.task, o) })).filter((i) => !i.task.deletedAt),
  }));
}

export function useSnoozed() {
  const api = useApi();
  const q = useQuery({ queryKey: qk.snoozed, queryFn: () => api.snoozed() });
  return useOverlaid(q, (v, o) => applyPatches(v, o));
}

export function useSettled() {
  const api = useApi();
  return useQuery({ queryKey: qk.settled, queryFn: () => api.settled() });
}

export function useSearch(q: string) {
  const qc = useQueryClient();
  const api = useApi();
  const term = q.trim();
  const query = useQuery({
    queryKey: qk.search(term),
    queryFn: async ({ signal }) => {
      const fetchStartedAt = Date.now();
      return { ...await api.search(term, signal), fetchStartedAt };
    },
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5_000,
  });
  // Containers in results show pending renames too.
  const pending = useContainerOverlay();
  const overlay = useOverlay();
  const startedAt = fetchStartedAt(query.data);
  useEffect(() => pruneContainerOverlay(qc), [qc, query.data]);
  const data = useMemo(() => {
    const v = query.data;
    if (!v) return v;
    return {
      ...v,
      tasks: applyPatches(v.tasks, overlay),
      areas: applyContainerOverlay("area", v.areas, pending, startedAt),
      spaces: applyContainerOverlay("space", v.spaces, pending, startedAt),
      projects: applyContainerOverlay("project", v.projects, pending, startedAt),
    };
  }, [query.data, overlay, pending, startedAt]);
  return { ...query, data };
}

export function useSettings() {
  const api = useApi();
  return useQuery({ queryKey: qk.settings, queryFn: () => api.settings() });
}

export function useArchived(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: qk.archived,
    enabled,
    queryFn: async () => {
      const [areas, spaces, projects] = await Promise.all([api.areas(true), api.spaces(true), api.projects(true)]);
      return {
        areas: areas.filter((a) => a.archivedAt),
        spaces: spaces.filter((s) => s.archivedAt),
        projects: projects.filter((p) => p.archivedAt),
      };
    },
  });
}

/** Inbox count for the sidebar (open, unsnoozed). */
export function useInboxCount() {
  const { tasks } = useTaskList({ scope: "inbox", sort: "created" }, (t) => !t.areaId && !t.spaceId);
  return tasks.filter((t) => t.status !== "done" && t.status !== "cancelled" && !t.flags.isSnoozed).length;
}
